/**
 * services/rules/rules.ts
 * Business logic for rules — listing (joined with per-rule stats), create /
 * edit, activate / deactivate, and the live deterministic preview. Screens call
 * these inside try/catch; the service owns mapping, the API call and the
 * envelope. Reads/writes fall back to the demo seed via withFallback.
 */
import { axiosInstance, unwrap, withFallback, runWrite } from '../client';
import {
  Upsell_Rules_Url,
  Upsell_Rule_Url,
  Upsell_Rule_Activate_Url,
  Upsell_Rule_Deactivate_Url,
  Upsell_Rule_Delete_Url,
  Upsell_Rule_Restore_Url,
  Upsell_Rule_Preview_Url,
  Upsell_Stats_Url,
} from '../../api-endpoints/endpoints';
import { getCapabilities } from '../capabilities/capabilities';
import { Rule, BuilderDraft, RuleBody } from '../../schema/rules/rule';
import { Stats } from '../../schema/stats';
import { PreviewResult, PreviewCandidate, DropReason } from '../../schema/preview';
import { Seed, SeedItem } from '../_demo/seed';
import { getDemoVertical } from '../_demo/verticalStore';

/* ── Builder draft → canonical wire body ───────────────────────────────────── */
export function toRuleBody(d: BuilderDraft): RuleBody {
  const all = [
    { cart_contains_category: d.triggerCat },
    ...(d.excludeCat ? [{ cart_not_contains_category: d.excludeCat }] : []),
  ];
  return {
    name: d.name.trim() || 'Untitled rule',
    motion: d.motion,
    trigger: { all },
    recommend: { from: { category: d.fromValue }, select: d.select, max_suggestions: d.maxSuggestions },
    constraints: {
      must_be_available: d.mustAvailable,
      max_price_delta_pct: Number(d.maxPriceDelta),
      compatibility: d.dietaryMatch ? ['compatibility_match'] : [],
      exclude_if_in_cart: true,
    },
    phrasing: { template: d.phrasing, tone: d.tone },
  };
}

/* ── Wire rule + stats → normalized Rule (UI shape) ────────────────────────── */
interface WireRule {
  id: string;
  name: string;
  motion: string;
  status: Rule['status'];
  source: Rule['source'];
  priority: number;
  trigger?: { all?: Array<Record<string, unknown>>; any?: Array<Record<string, unknown>> };
  recommend?: { from?: { category?: string; relation?: string; tags?: string[]; explicit?: string[] } };
  superseded_by?: string | null;
  rationale?: string | null;
  dormant?: boolean;
  dormant_reason?: string;
  deleted?: boolean;
}

function triggerSummary(trigger: WireRule['trigger'], nameOf: (id: string) => string): string {
  const conds = [...(trigger?.all ?? []), ...(trigger?.any ?? [])];
  const parts: string[] = [];
  for (const c of conds) {
    if (c.cart_contains_category) parts.push(`Cart has ${nameOf(String(c.cart_contains_category))}`);
    else if (c.cart_contains_item) parts.push(`Cart has ${nameOf(String(c.cart_contains_item))}`);
    else if (c.cart_not_contains_category) parts.push(`no ${nameOf(String(c.cart_not_contains_category))}`);
    else if (c.cart_total_gte) parts.push(`Cart total ≥ ${String(c.cart_total_gte)}`);
  }
  return parts.join(', ') || 'Any cart';
}

/** Plain-English sentence a shop owner can read — matches the AI-Proposals
 *  phrasing: "When a customer buys X, suggest Y." */
function humanSentence(w: WireRule, nameOf: (id: string) => string): string {
  const conds = [...(w.trigger?.all ?? []), ...(w.trigger?.any ?? [])];
  const buys: string[] = [];
  const notHas: string[] = [];
  let threshold: number | null = null;
  for (const c of conds) {
    if (typeof c.cart_contains_category === 'string') buys.push(nameOf(c.cart_contains_category));
    else if (typeof c.cart_contains_item === 'string') buys.push(nameOf(c.cart_contains_item));
    else if (typeof c.cart_not_contains_category === 'string') notHas.push(nameOf(c.cart_not_contains_category));
    else if (typeof c.cart_total_gte === 'number') threshold = c.cart_total_gte;
  }
  const from = w.recommend?.from;
  // An explicit or tag pool is as nameable as a category — without these a rule
  // like "Upsize the fries" degraded to the bare "make a suggestion".
  const suggestName = from?.category
    ? nameOf(from.category)
    : from?.explicit?.length
      ? from.explicit.map(nameOf).join(' or ')
      : from?.tags?.length
        ? from.tags.join(' or ')
        : from?.relation;
  const suggestText = suggestName ? `suggest ${suggestName}` : 'make a suggestion';
  let when: string;
  if (buys.length) {
    when = `a customer buys ${buys.join(' and ')}`;
    if (notHas.length) when += ` but not ${notHas.join(' or ')}`;
    if (threshold != null) when += ` and the total reaches ₹${threshold}`;
  } else if (threshold != null) {
    when = `the order total reaches ₹${threshold}`;
  } else {
    when = 'a customer is ordering';
  }
  return `When ${when}, ${suggestText}.`;
}

/** Every category id named in a rule (trigger + suggestion), de-duped. */
function referencedCategoryIds(w: WireRule): string[] {
  const ids: string[] = [];
  const conds = [...(w.trigger?.all ?? []), ...(w.trigger?.any ?? [])];
  for (const c of conds) {
    if (typeof c.cart_contains_category === 'string') ids.push(c.cart_contains_category);
    else if (typeof c.cart_not_contains_category === 'string') ids.push(c.cart_not_contains_category);
  }
  if (w.recommend?.from?.category) ids.push(w.recommend.from.category);
  return [...new Set(ids)];
}

type CatInfo = Map<string, { name: string; items: string[] }>;

function joinRulesWithStats(wire: WireRule[], stats: Stats | null, nameOf: (id: string) => string, catInfo: CatInfo): Rule[] {
  const byId = new Map(stats?.rules.map((r) => [r.rule_id, r]) ?? []);
  return wire.map((w) => {
    const s = byId.get(w.id);
    const categoryExamples = referencedCategoryIds(w)
      .map((id) => catInfo.get(id))
      .filter((c): c is { name: string; items: string[] } => !!c && c.items.length > 0);
    return {
      id: w.id,
      name: w.name,
      motion: w.motion,
      status: w.status,
      source: w.source,
      priority: w.priority,
      triggerText: triggerSummary(w.trigger, nameOf),
      intentText: humanSentence(w, nameOf),
      fromText: w.recommend?.from?.category
        ? nameOf(w.recommend.from.category)
        : w.recommend?.from?.relation ?? 'a match',
      categoryExamples,
      select: '',
      acceptRate: s ? s.accept_rate : null,
      trend: s ? s.trend : null,
      cold: s?.cold_start ?? false,
      dormant: w.dormant ?? false,
      ...(w.dormant_reason ? { dormantReason: w.dormant_reason } : {}),
      deleted: w.deleted ?? false,
      superseded: !!w.superseded_by,
      supersededBy: w.superseded_by ?? undefined,
    };
  });
}

export function listRules(): Promise<Rule[]> {
  return withFallback(
    async () => {
      const [rulesRes, statsRes, caps] = await Promise.all([
        axiosInstance.get(Upsell_Rules_Url),
        axiosInstance.get(Upsell_Stats_Url).catch(() => null),
        getCapabilities().catch(() => null),
      ]);
      const wire = unwrap<WireRule[]>(rulesRes.data);
      const stats = statsRes ? unwrap<Stats>(statsRes.data) : null;
      // category_id → human name, so rules read "Cart has Mains" not a raw UUID.
      const catName = new Map((caps?.cats ?? []).map((c) => [c.value, c.label] as const));
      // item_id → item name, for rules written against a single item
      // (cart_contains_item, an explicit pool), which otherwise showed the uuid.
      const itemName = new Map((caps?.items ?? []).map((i) => [i.value, i.label] as const));
      const nameOf = (id: string): string => catName.get(id) ?? itemName.get(id) ?? id;
      // category_id → { name, example items } for the operator preview line.
      const catInfo: CatInfo = new Map(
        (caps?.cats ?? []).map((c) => [c.value, { name: c.label, items: c.examples ?? [] }] as const),
      );
      return joinRulesWithStats(wire, stats, nameOf, catInfo);
    },
    () => Seed.rules(getDemoVertical()),
  );
}

export function createRule(draft: BuilderDraft, activate: boolean): Promise<void> {
  return runWrite(
    async () => {
      const { data } = await axiosInstance.post(Upsell_Rules_Url, { ...toRuleBody(draft), priority: Number(draft.priority) || undefined });
      const created = unwrap<{ id: string }>(data);
      if (activate && created?.id) await axiosInstance.post(Upsell_Rule_Activate_Url(created.id));
    },
    () => undefined,
  );
}

export function updateRule(id: string, draft: BuilderDraft, activate: boolean): Promise<void> {
  return runWrite(
    async () => {
      await axiosInstance.patch(Upsell_Rule_Url(id), { ...toRuleBody(draft), priority: Number(draft.priority) || undefined });
      if (activate) await axiosInstance.post(Upsell_Rule_Activate_Url(id));
    },
    () => undefined,
  );
}

export function activateRule(id: string): Promise<void> {
  return runWrite(
    async () => {
      await axiosInstance.post(Upsell_Rule_Activate_Url(id));
    },
    () => undefined,
  );
}

export function deactivateRule(id: string): Promise<void> {
  return runWrite(
    async () => {
      await axiosInstance.post(Upsell_Rule_Deactivate_Url(id));
    },
    () => undefined,
  );
}

/** Soft-delete → moves to the Deleted tab, kept in the DB. */
export function deleteRule(id: string): Promise<void> {
  return runWrite(
    async () => {
      await axiosInstance.post(Upsell_Rule_Delete_Url(id));
    },
    () => undefined,
  );
}

/** Restore a deleted rule → back to Deactivated. */
export function restoreRule(id: string): Promise<void> {
  return runWrite(
    async () => {
      await axiosInstance.post(Upsell_Rule_Restore_Url(id));
    },
    () => undefined,
  );
}

/* ── Preview ───────────────────────────────────────────────────────────────── */
export function previewRule(draft: BuilderDraft): Promise<PreviewResult> {
  return withFallback(
    async () => {
      const { data } = await axiosInstance.post(Upsell_Rule_Preview_Url, { rule: toRuleBody(draft) });
      return unwrap<PreviewResult>(data);
    },
    () => seedPreview(draft),
  );
}

/** Deterministic preview against the seed catalog (mirrors the engine's filter→pick). */
function seedPreview(draft: BuilderDraft): PreviewResult {
  const vertical = getDemoVertical();
  const cat = Seed.catalog(vertical);
  const trig = cat.find((i) => i.cat === draft.triggerCat) ?? cat[0];
  const pool = cat.filter((i) => i.cat === draft.fromValue && i.id !== trig.id);

  const eligible: SeedItem[] = [];
  const dropped: Array<{ item: SeedItem; reason: DropReason }> = [];
  for (const i of pool) {
    if (draft.mustAvailable && !i.avail) {
      dropped.push({ item: i, reason: 'unavailable' });
    } else if (draft.dietaryMatch && trig.diet === 'veg' && i.diet === 'non_veg') {
      dropped.push({ item: i, reason: 'dietary' });
    } else if (draft.maxPriceDelta && i.price > trig.price * (1 + Number(draft.maxPriceDelta) / 100)) {
      dropped.push({ item: i, reason: 'price_delta' });
    } else {
      eligible.push(i);
    }
  }

  const ordered = [...eligible];
  if (draft.select === 'attach_rate') ordered.sort((a, b) => b.attach - a.attach);
  else if (draft.select === 'most_popular') ordered.sort((a, b) => b.pop - a.pop);
  else if (draft.select === 'price_up_nearest') {
    const up = ordered.filter((i) => i.price > trig.price).sort((a, b) => a.price - b.price);
    ordered.splice(0, ordered.length, ...(up.length ? up : ordered.sort((a, b) => a.price - b.price)));
  }
  const pick = ordered[0] ?? null;

  let reason = 'First in your curated order.';
  if (draft.select === 'attach_rate') reason = `Highest attach-rate (${Math.round((pick?.attach ?? 0) * 100)}%) of ${eligible.length} eligible.`;
  else if (draft.select === 'most_popular') reason = `Most popular of ${eligible.length} eligible.`;
  else if (draft.select === 'price_up_nearest') reason = 'Nearest price step-up from the trigger.';

  const candidate_pool: PreviewCandidate[] = [
    ...ordered.map((i) => ({ item_id: i.id, name: i.name, price: i.price, eligible: true })),
    ...dropped.map((d) => ({ item_id: d.item.id, name: d.item.name, price: d.item.price, eligible: false, drop_reason: d.reason })),
  ];

  const phrasing = (draft.phrasing || '')
    .replace(/\{item_name\}/g, pick?.name ?? '…')
    .replace(/\{trigger_item_name\}/g, trig.name);

  return {
    matched: !!pick,
    trigger: { item_id: trig.id, name: trig.name, price: trig.price },
    candidate_pool,
    pick: pick ? { item_id: pick.id, name: pick.name, price: pick.price, phrasing } : null,
    pick_reason: pick ? reason : 'No eligible candidate — the engine stays silent.',
  };
}
